import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { roomsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Rooms.css';

const Rooms = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newRoom, setNewRoom] = useState({
    name: '',
    description: '',
    isPublic: false,
    maxParticipants: 50
  });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadRooms();
  }, [filter]);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const response = await roomsAPI.getAll(filter);
      setRooms(response.data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      const response = await roomsAPI.create(newRoom);
      setShowModal(false);
      setNewRoom({ name: '', description: '', isPublic: false, maxParticipants: 50 });
      loadRooms();
      navigate(`/room/${response.data.id}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette room ?')) return;
    try {
      await roomsAPI.deleteRoom(roomId);
      loadRooms();
    } catch (err) {
      setError(err.message);
    }
  };

const handleJoinRoom = async (roomId) => {
     try {
       await roomsAPI.joinRoom(roomId);
       // Check if user is the owner
       const room = rooms.find(r => r.id === roomId);
       const isOwner = room?.ownerId === user?.id;
       if (isOwner) {
         // Owner can join directly
         navigate(`/room/${roomId}`);
       } else {
         // Redirect to waiting room instead of directly joining
         navigate(`/rooms/${roomId}/waiting`);
       }
     } catch (err) {
       setError(err.message);
     }
   };

  if (loading) return <div className="loading">Chargement...</div>;

  return (
    <div className="rooms-page">
      <div className="rooms-header">
        <h1>Mes Rooms</h1>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Créer une Room
        </button>
      </div>

      <div className="rooms-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Toutes
        </button>
        <button 
          className={`filter-btn ${filter === 'owned' ? 'active' : ''}`}
          onClick={() => setFilter('owned')}
        >
          Mes Rooms
        </button>
        <button 
          className={`filter-btn ${filter === 'public' ? 'active' : ''}`}
          onClick={() => setFilter('public')}
        >
          Rooms Publiques
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {rooms.length === 0 ? (
        <div className="empty-state">
          <p>Aucune room trouvée.</p>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            Créer votre première room
          </button>
        </div>
      ) : (
        <div className="rooms-grid">
          {rooms.map(room => (
            <div key={room.id} className="room-card">
              <div className="room-info">
                <h3>{room.name}</h3>
                {room.description && <p className="room-description">{room.description}</p>}
                <div className="room-meta">
                  <span>{room.isPublic ? '🔓 Publique' : '🔒 Privée'}</span>
                  <span>{room.maxParticipants} participants max</span>
                </div>
              </div>
<div className="room-actions">
                 <button 
                   className="btn-secondary"
                   onClick={() => navigate(`/rooms/${room.id}`)}
                 >
                   Gérer
                 </button>
                 {room.ownerId === user?.id && (
                   <>
                     <button 
                       className="btn-danger"
                       onClick={() => handleDeleteRoom(room.id)}
                     >
                       Supprimer
                     </button>
                     <span className="owner-badge" style={{fontSize: '12px', color: '#4a90e2'}}>Propriétaire</span>
                   </>
                 )}
                 {!room.isParticipant && room.ownerId !== user?.id && (
                   <button 
                     className="btn-primary"
                     onClick={() => handleJoinRoom(room.id)}
                   >
                     Rejoindre
                   </button>
                 )}
               </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Créer une Room</h2>
            <form onSubmit={handleCreateRoom}>
              <div className="form-group">
                <label>Nom de la room *</label>
                <input
                  type="text"
                  value={newRoom.name}
                  onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                  required
                  placeholder="Nom de votre room"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newRoom.description}
                  onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                  placeholder="Description optionnelle"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newRoom.isPublic}
                    onChange={(e) => setNewRoom({ ...newRoom, isPublic: e.target.checked })}
                  />
                  Room publique (visible par tous les utilisateurs)
                </label>
              </div>
              <div className="form-group">
                <label>Nombre maximum de participants</label>
                <input
                  type="number"
                  value={newRoom.maxParticipants}
                  onChange={(e) => {
                    const value = e.target.value;
                    const parsed = parseInt(value);
                    setNewRoom({ ...newRoom, maxParticipants: isNaN(parsed) ? 50 : parsed });
                  }}
                  min={2}
                  max={100}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rooms;
